import { Injectable, InjectionToken, Signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { isEqual } from 'lodash-es';
import {
  BehaviorSubject,
  Observable,
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  of,
  shareReplay,
} from 'rxjs';
import { SessionStorageService } from 'src/app/core/services/cache/storage.service';
import { AzureFeatureFlagService } from './azure-feature-flag.service';

export type FeatureFlagKey =
  | 'SIDRL'
  | 'OldReviewPage'
  | 'Terminology'
  | 'BatchDownload'
  | 'GlobalDashboardView'
  | 'ProjectBasicSetupClassifications'
  | 'ProjectBasicSetupIndexDefinitions'
  | 'ReleaseBanner'
  | 'TransmittalOldTable'
  | 'SingleUserAgGrid'
  | 'WorksheetViewModeOptions'
  | 'SubscriptionMatrixViewOptions'; // Define feature toggle keys here
type FeatureFlagValue = boolean;
export type FeatureFlag = { [key in FeatureFlagKey]: FeatureFlagValue };
export type FeatureFlagConfig = {
  [key in FeatureFlagKey]: {
    value: FeatureFlagValue;
    remote: FeatureFlagValue;
    override: FeatureFlagValue | undefined;
  };
};

export interface FeatureFlagAdapter {
  allFlags$: Observable<FeatureFlag>;
}

const BROWSER_STORAGE_KEY_FEATURE_TOGGLE_OVERRIDE = 'featureToggleOverride';
const DEFAULT_FEATURE_FLAG_CONFIG: FeatureFlag = {
  SIDRL: false,
  OldReviewPage: false,
  Terminology: false,
  BatchDownload: false,
  GlobalDashboardView: false,
  ProjectBasicSetupClassifications: false,
  ProjectBasicSetupIndexDefinitions: false,
  ReleaseBanner: false,
  TransmittalOldTable: false, // Default value for the new feature toggle
  SingleUserAgGrid: false,
  WorksheetViewModeOptions: true,
  SubscriptionMatrixViewOptions: false,
};

const FEATURE_FLAG_ADAPTER = new InjectionToken<FeatureFlagAdapter>('FeatureFlagAdapter', {
  factory: () => inject(AzureFeatureFlagService),
});

@Injectable({ providedIn: 'root' })
export class FeatureToggleService {
  readonly #browserStorage = inject(SessionStorageService);
  readonly #localFlagsOverride$ = new BehaviorSubject<Partial<FeatureFlag>>(
    JSON.parse(this.#browserStorage.get(BROWSER_STORAGE_KEY_FEATURE_TOGGLE_OVERRIDE) ?? '{}'),
  );
  readonly #remoteFlags = inject(FEATURE_FLAG_ADAPTER);

  readonly allToggles$: Observable<FeatureFlagConfig> = combineLatest([
    this.#remoteFlags.allFlags$.pipe(
      catchError(() => of(DEFAULT_FEATURE_FLAG_CONFIG)),
      distinctUntilChanged(isEqual),
    ),
    this.#localFlagsOverride$.asObservable().pipe(distinctUntilChanged(isEqual)),
  ]).pipe(
    map(([flags, overrides]) => {
      const resolvedFlags: FeatureFlag = { ...DEFAULT_FEATURE_FLAG_CONFIG, ...flags };

      return Object.fromEntries(
        Object.entries(resolvedFlags).map(([key, value]) => [
          key as FeatureFlagKey,
          { value: overrides[key] ?? value, remote: value, override: overrides[key] },
        ]),
      ) as FeatureFlagConfig;
    }),
    shareReplay({ bufferSize: 1, refCount: false }),
  );
  readonly allToggles = toSignal(this.allToggles$);

  constructor() {
    this.#localFlagsOverride$
      .asObservable()
      .subscribe(next =>
        this.#browserStorage.set(BROWSER_STORAGE_KEY_FEATURE_TOGGLE_OVERRIDE, JSON.stringify(next)),
      );
  }

  get(key: FeatureFlagKey): Observable<boolean> {
    return this.allToggles$.pipe(map(toggles => toggles[key]?.value ?? DEFAULT_FEATURE_FLAG_CONFIG[key]));
  }

  getAsSignal(key: FeatureFlagKey): Signal<boolean> {
    return toSignal(this.get(key), { initialValue: DEFAULT_FEATURE_FLAG_CONFIG[key] });
  }

  toggleOverride(key: FeatureFlagKey): void {
    const currentValue = this.#localFlagsOverride$.getValue()[key];
    let nextValue;
    switch (currentValue) {
      case undefined: {
        nextValue = true;
        break;
      }
      case true: {
        nextValue = false;
        break;
      }
      case false: {
        nextValue = undefined;
        break;
      }
    }

    this.#localFlagsOverride$.next({ ...this.#localFlagsOverride$.getValue(), [key]: nextValue });
  }
}
